import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { useMenuStore } from '@/stores/MenuStore'
import { getCookie } from '@/utilits/getCookie'
import { deleteCookieToken } from '@/utilits/deleteCookieToken'
import { getFirebaseToken } from '@/utilits/getFirebaseToken'
import { getHealthAccessStatus, requestHealthAuthorization } from '@/utilits/getHealth'
import { postLog } from '@/api/postLog'
import { useBiometry } from '@/composables/useBiometry'
import { usePlatform } from '@/composables/usePlatform'

export const useUserStore = defineStore('user', () => {
  const isHealthAllowed = ref(process.env.VUE_APP_HEALTH === 'true')
  const user = ref(null)
  const userLoading = ref(false)
  const userAppoints = ref(null)
  const userToken = ref(null)
  const userCards = ref(null)
  const userSmsCode = ref(null)
  const userPhone = ref(null)
  const userPrimaryId = ref(null)
  const hasPinCode = ref(null)
  const isForcedSms = ref(false)
  const isHealthAvailable = ref(null)
  const router = useRouter()
  const menuStore = useMenuStore()
  const onAuthorizedActions = ref([])
  const { isBiometryActive, setBiometry, initializeBiometry } = useBiometry()
  const { isPhone } = usePlatform()
  const { closeMenu } = menuStore

  const userTokenStorage = computed(() => {
    return getCookie('user_token')
  })

  const getUserId = computed(() => {
    if (!user.value) return null

    return user.value.id
  })

  const updateUserFamilyCards = (cards) => {
    user.value.family_cards = [...cards]
    user.value.family_cards.unshift({ ...user.value })
  }

  const changeUserToFamilyMember = (member) => {
    for (const memberKey in member) {
      user.value[memberKey] = member[memberKey]
    }
  }

  const userFullName = computed(() => {
    if (!user.value) return ''

    const lastName = user.value?.last_name || ''
    const firstName = user.value?.first_name || ''
    const middleName = user.value?.middle_name || ''
    return `${lastName} ${firstName} ${middleName}`
  })

  const isAdmin = computed(() => {
    if (!user.value) return null
    return user.value.is_admin
  })

  const setUserCards = (cards) => {
    userCards.value = cards
  }
  const setUserSmsCode = (code) => {
    userSmsCode.value = code
  }

  const setUserPhone = (phone) => {
    if (phone === false) {
      window.localStorage.removeItem('user_phone')
      userPhone.value = ''
    } else {
      window.localStorage.setItem('user_phone', phone)
      userPhone.value = phone
    }
  }

  const setHasPinCode = (val) => {
    hasPinCode.value = val
  }
  const setIsForcedSms = (val) => {
    isForcedSms.value = val
  }
  const setIsSubscribed = (val) => {
    user.value.is_unsubscribe = val
  }

  const setUserToken = (token) => {
    const twoWeeksInMs = 3600 * 24 * 14
    document.cookie = `user_token=${token}; max-age=${twoWeeksInMs}; path=/; secure`
    window.localStorage.setItem('user_token', token)
    userToken.value = token
  }

  const addOnAuthorizedAction = (action) => {
    onAuthorizedActions.value.push(action)
  }

  const logOut = () => {
    setUserPhone(false)
    setBiometry(null)
    deleteCookieToken()
    userToken.value = null
    user.value = null
    closeMenu()
    router.push({
      name: 'auth-phone'
    })
  }

  const setUser = async (token) => {
    userLoading.value = true
    try {
      const response = await axios.get('/v1/user/index', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data.status === 'ok') {
        userPrimaryId.value = response.data.data.id
        user.value = { ...response.data.data }

        if (Array.isArray(user.value.family_cards)) {
          user.value.family_cards.unshift({ ...response.data.data })
        }
      }
    } catch (err) {
      postLog({
        m: 'setUser ERROR inside UserStore',
        err: err
      })
      if (err.response.status === 401) {
        logOut()
      } else {
        throw new Error(err)
      }
    } finally {
      userLoading.value = false
    }
  }

  const setUserAvatar = (newPhoto) => {
    user.value.photo = newPhoto
  }

  watch(getUserId, async () => {
    if (getUserId.value) {
      onAuthorizedActions.value.forEach((action) => {
        action()
      })

      onAuthorizedActions.value = []
    }

    if (getUserId.value && isPhone.value) {
      await getFirebaseToken()

      if (isHealthAllowed.value) {
        const isHealthInitialized = JSON.parse(localStorage.getItem('health_initialized'))
        if (!isHealthInitialized) {
          isHealthAvailable.value = await requestHealthAuthorization()
        } else {
          isHealthAvailable.value = await getHealthAccessStatus()
        }
      }

      if (isBiometryActive.value === null) {
        await initializeBiometry(userToken.value)
      }
    }
  }, { immediate: true })

  return {
    user,
    setUserToken,
    userToken,
    setUser,
    userTokenStorage,
    logOut,
    getUserId,
    setUserCards,
    setIsSubscribed,
    setUserSmsCode,
    userCards,
    userSmsCode,
    setUserPhone,
    userPhone,
    setHasPinCode,
    hasPinCode,
    userFullName,
    setUserAvatar,
    changeUserToFamilyMember,
    updateUserFamilyCards,
    addOnAuthorizedAction,
    userAppoints,
    isHealthAllowed,
    isAdmin,
    userPrimaryId,
    isHealthAvailable,
    isForcedSms,
    userLoading,
    setIsForcedSms
  }
})
